import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, Package, Layers, Tags, Boxes, ShoppingCart, Users,
  Star, HelpCircle, Leaf, FlaskConical, Home, FileText, Image,
  BookOpen, Percent, UserPlus, Truck, Receipt, CreditCard, Plug,
  Palette, Search, BarChart3, LineChart, Shield, Settings, ScrollText,
  Megaphone, LogOut, ChevronDown, Menu, X, MailOpen, Scale,
} from "lucide-react";
import { BrandLogo } from "@/components/BrandMark";
import usePageTitle from "@/hooks/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "hk_admin_pw";

// --- All admin sidebar items — grouped so it reads like a real ops console ---
export const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
    ],
  },
  {
    label: "Catalogue",
    items: [
      { to: "/admin/products", icon: Package, label: "Products" },
      { to: "/admin/categories", icon: Layers, label: "Categories" },
      { to: "/admin/collections", icon: Tags, label: "Collections" },
      { to: "/admin/inventory", icon: Boxes, label: "Inventory" },
    ],
  },
  {
    label: "Sales & CX",
    items: [
      { to: "/admin/orders", icon: ShoppingCart, label: "Orders" },
      { to: "/admin/enquiries", icon: Megaphone, label: "Enquiries" },
      { to: "/admin/customers", icon: Users, label: "Customers" },
      { to: "/admin/reviews", icon: Star, label: "Reviews" },
    ],
  },
  {
    label: "Content",
    items: [
      { to: "/admin/homepage", icon: Home, label: "Homepage Editor" },
      { to: "/admin/pages", icon: FileText, label: "Pages & Content" },
      { to: "/admin/legal", icon: Scale, label: "Legal Pages" },
      { to: "/admin/faqs", icon: HelpCircle, label: "FAQs" },
      { to: "/admin/botanicals", icon: Leaf, label: "Botanicals" },
      { to: "/admin/ingredients", icon: FlaskConical, label: "Ingredients" },
      { to: "/admin/banners", icon: Megaphone, label: "Banners" },
      { to: "/admin/media", icon: Image, label: "Media Library" },
      { to: "/admin/blog", icon: BookOpen, label: "Blog" },
    ],
  },
  {
    label: "Marketing",
    items: [
      { to: "/admin/coupons", icon: Percent, label: "Coupons" },
      { to: "/admin/influencers", icon: UserPlus, label: "Influencers" },
      { to: "/admin/subscribers", icon: MailOpen, label: "Subscribers" },
    ],
  },
  {
    label: "Commerce Setup",
    items: [
      { to: "/admin/shipping", icon: Truck, label: "Shipping" },
      { to: "/admin/logistics", icon: Truck, label: "Logistics / Couriers" },
      { to: "/admin/tax", icon: Receipt, label: "Tax / GST" },
      { to: "/admin/payments", icon: CreditCard, label: "Payment Gateways" },
      { to: "/admin/integrations", icon: Plug, label: "Integrations" },
    ],
  },
  {
    label: "Brand & SEO",
    items: [
      { to: "/admin/logo", icon: Image, label: "Logo Management" },
      { to: "/admin/brand", icon: Palette, label: "Brand Settings" },
      { to: "/admin/seo", icon: Search, label: "SEO & Social" },
    ],
  },
  {
    label: "Insights",
    items: [
      { to: "/admin/reports", icon: BarChart3, label: "Reports" },
      { to: "/admin/analytics", icon: LineChart, label: "Analytics" },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/admin/users", icon: Users, label: "User Management" },
      { to: "/admin/roles", icon: Shield, label: "Roles & Permissions" },
      { to: "/admin/settings", icon: Settings, label: "General Settings" },
      { to: "/admin/logs", icon: ScrollText, label: "System Logs" },
    ],
  },
];

/** Password prompt for first-visit admins. */
const LoginScreen = ({ onLoggedIn }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return setErr("Password required");
    setBusy(true);
    setErr("");
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Password": pw } });
      sessionStorage.setItem(STORAGE_KEY, pw);
      toast.success("Signed in");
      // Notify any admin pages already mounted (they refetch on this event)
      window.dispatchEvent(new Event("hk-admin-auth"));
      onLoggedIn(pw);
    } catch (e) {
      setErr("Invalid password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--hk-ivory)] px-6">
      <form
        onSubmit={submit}
        noValidate
        className="w-full max-w-md hk-card p-10"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-3 mb-6">
          <BrandLogo variant="light" height={40} markSize={40} wordSize={18} showTagline={false} testId="admin-login-brand-logo" />
          <div className="ml-auto text-right">
            <h1 className="hk-h3">Admin Console</h1>
          </div>
        </div>
        <label className="text-[12px] uppercase tracking-widest text-[var(--hk-ink-2)]">Password</label>
        <input
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          data-testid="admin-login-password"
          className="mt-2 w-full h-11 px-3 rounded-md border border-[var(--hk-border)] bg-white/70 focus:outline-none focus:border-[var(--hk-ink)]"
        />
        {err && <p className="text-xs text-red-600 mt-2" data-testid="admin-login-error">{err}</p>}
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-login-btn"
          className="hk-btn-primary mt-6 w-full justify-center"
        >
          {busy ? "Verifying…" : "Enter Admin"}
        </button>
      </form>
    </div>
  );
};

const NavItem = ({ to, icon: Icon, label, end, current, onClick }) => {
  const active = end ? current === to : current === to || current.startsWith(to + "/");
  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid={`nav-${to.replace(/\//g, "-")}`}
      className={
        "flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13.5px] transition-colors " +
        (active
          ? "bg-[var(--hk-ink)] text-[var(--hk-ivory)]"
          : "text-[var(--hk-ink-2)] hover:bg-[var(--hk-warm-ivory)] hover:text-[var(--hk-ink)]")
      }
    >
      <Icon size={16} className={active ? "opacity-100" : "opacity-70"} />
      <span className="truncate">{label}</span>
    </Link>
  );
};

const Sidebar = ({ open, setOpen, current }) => {
  const nav = useNavigate();
  const signOut = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    toast.success("Signed out");
    nav("/admin");
    setTimeout(() => window.location.reload(), 300);
  };
  return (
    <>
      {/* mobile overlay */}
      {open && (
        <button
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setOpen(false)}
          aria-label="close menu"
        />
      )}
      <aside
        className={
          "fixed lg:sticky top-0 z-40 h-screen bg-[var(--hk-ivory)] border-r border-[var(--hk-border)] transition-transform " +
          "w-[260px] flex flex-col " +
          (open ? "translate-x-0" : "-translate-x-full lg:translate-x-0")
        }
        data-testid="admin-sidebar"
      >
        <div className="px-5 pt-5 pb-4 flex items-center gap-3 border-b border-[var(--hk-border)]">
          <div className="min-w-0 flex-1">
            <BrandLogo variant="light" height={32} markSize={34} wordSize={16} showTagline={false} testId="admin-sidebar-brand-logo" />
            <p className="mt-1.5 text-[10px] tracking-[0.28em] uppercase text-[var(--hk-ink-2)]">Admin Console</p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="ml-auto lg:hidden p-1.5 rounded-md text-[var(--hk-ink-2)] hover:bg-[var(--hk-warm-ivory)]"
            aria-label="close sidebar"
          >
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {NAV_GROUPS.map((g) => (
            <div key={g.label}>
              <p className="text-[10px] tracking-[0.28em] uppercase text-[var(--hk-ink-3,#8a8479)] px-2 mb-1.5">
                {g.label}
              </p>
              <div className="space-y-0.5">
                {g.items.map((it) => (
                  <NavItem key={it.to} {...it} current={current} onClick={() => setOpen(false)} />
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="px-3 py-3 border-t border-[var(--hk-border)]">
          <button
            onClick={signOut}
            data-testid="admin-sign-out"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] text-[var(--hk-ink-2)] hover:bg-[var(--hk-warm-ivory)]"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </aside>
    </>
  );
};

/** Main admin layout wrapping every /admin/* page. */
const AdminLayout = ({ title, subtitle, actions, children }) => {
  const pw = sessionStorage.getItem(STORAGE_KEY) || "";
  const [authed, setAuthed] = useState(!!pw);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  usePageTitle(`Admin · ${title || "Console"}`);

  useEffect(() => {
    if (!pw) return;
    // verify still-valid session
    axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Password": pw } }).catch(() => {
      sessionStorage.removeItem(STORAGE_KEY);
      setAuthed(false);
    });
  }, [pw]);

  if (!authed) return <LoginScreen onLoggedIn={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen flex bg-[var(--hk-warm-ivory)]">
      <Sidebar open={open} setOpen={setOpen} current={location.pathname} />
      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 flex items-center gap-3 px-5 lg:px-10 py-4 bg-[var(--hk-warm-ivory)]/95 backdrop-blur border-b border-[var(--hk-border)]">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="lg:hidden p-2 rounded-md border border-[var(--hk-border)] text-[var(--hk-ink)]"
            aria-label="open sidebar"
          >
            <Menu size={16} />
          </button>
          <div className="min-w-0">
            <p className="hk-eyebrow">Admin</p>
            <h1 className="hk-h3 truncate" data-testid="admin-page-title">{title}</h1>
            {subtitle && <p className="text-[13px] text-[var(--hk-ink-2)] mt-0.5">{subtitle}</p>}
          </div>
          <div className="ml-auto flex items-center gap-2" data-testid="admin-page-actions">{actions}</div>
        </header>
        <div className="px-5 lg:px-10 py-8 max-w-[1400px]" data-testid="admin-page-body">
          {children}
        </div>
      </main>
    </div>
  );
};

/** Returns the current admin session pw, and re-renders on login events. */
export const useAdminAuthed = () => {
  const [pw, setPw] = React.useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  React.useEffect(() => {
    const onAuth = () => setPw(sessionStorage.getItem(STORAGE_KEY) || "");
    window.addEventListener("hk-admin-auth", onAuth);
    window.addEventListener("storage", onAuth);
    return () => {
      window.removeEventListener("hk-admin-auth", onAuth);
      window.removeEventListener("storage", onAuth);
    };
  }, []);
  return pw;
};

/** Small helper hook exposing the persisted admin pw for pages that need it directly. */
export const useAdminPw = () => sessionStorage.getItem(STORAGE_KEY) || "";

export const adminHeaders = () => ({ "X-Admin-Password": sessionStorage.getItem(STORAGE_KEY) || "" });

export default AdminLayout;
